import { TamediaStyles } from "@ta-interaktiv/microfrontend-template-helpers";
import { useState, useRef } from 'react'
import styled from 'styled-components'
import { AutofitIFrame } from '@ta-interaktiv/react-autofit-iframe'
import { TextWidthExtended } from '@ta-interaktiv/react-disco-grid'
import { GlobalVariables } from 'Globals/GlobalVariables'
import { Toolbar } from 'components/Toolbar'
import Facts from 'components/Facts'

declare global {
  interface Window {
    scrollTarget: string | undefined
    currentStation: string | undefined
  }
}

window.scrollTarget = window.scrollTarget || undefined
window.currentStation = window.currentStation || undefined

const MainComponent = ({
  tenant,
  colorMode,
  properties,
  trackEvent,
}: MicroFrontendProps) => {
  const localDebugMode = window.location.hostname === 'localhost'

  // German as current default if localhost
  const lang = localDebugMode ? 'fr' : tenant.lang

  // TODO German version: add new button orders for tenants TA, BZ, Basler Zeitung
  const stations: Record<string, string> =
    lang === 'fr'
      ? tenant.name === 'Tribune de Genève'
        ? {
            GVE: 'Genève',
            CGI: 'Nyon',
            PUY: 'Lausanne',
            CHD: 'Château-d’Œx',
            PAY: 'Payerne',
            LUG: 'Lugano',
            SMA: 'Zurich',
          }
        : {
            PUY: 'Lausanne',
            GVE: 'Genève',
            CGI: 'Nyon',
            PAY: 'Payerne',
            CHD: 'Château-d’Œx',
            LUG: 'Lugano',
            SMA: 'Zurich',
          }
      : tenant.name === 'Tages-Anzeiger' ||
        tenant.name === 'Der Landbote' ||
        tenant.name === 'Zürichsee-Zeitung' ||
        tenant.name === 'Zürcher Unterländer'
      ? {
          SMA: 'Zürich',
          BAS: 'Basel',
          BER: 'Bern',
          LUZ: 'Luzern',
          STG: 'St. Gallen',
          GVE: 'Genf',
          LUG: 'Lugano',
        }
      : tenant.name === 'Berner Zeitung' ||
        tenant.name === 'Der Bund' ||
        tenant.name === 'Berner Oberländer' ||
        tenant.name === 'Langenthaler Tagblatt' ||
        tenant.name === 'Thuner Tagblatt'
      ? {
          BER: 'Bern',
          BAS: 'Basel',
          SMA: 'Zürich',
          LUZ: 'Luzern',
          STG: 'St. Gallen',
          GVE: 'Genf',
          LUG: 'Lugano',
        }
      : tenant.name === 'Basler Zeitung'
      ? {
          BAS: 'Basel',
          SMA: 'Zürich',
          BER: 'Bern',
          LUZ: 'Luzern',
          STG: 'St. Gallen',
          GVE: 'Genf',
          LUG: 'Lugano',
        }
      : {
          SMA: 'Zürich',
          BAS: 'Basel',
          BER: 'Bern',
          LUZ: 'Luzern',
          STG: 'St. Gallen',
          GVE: 'Genf',
          LUG: 'Lugano',
        }

  const station_locations: Record<string, string> =
    lang === 'fr'
      ? {
          PUY: 'Pully',
          PAY: 'Payerne',
          CGI: 'Nyon-Changins',
          CHD: 'Château-d’Œx',
          GVE: 'Cointrin',
          SMA: 'Fluntern',
        }
      : {
          SMA: 'Fluntern',
          BAS: 'Binningen',
          BER: 'Zollikofen',
          GVE: 'Cointrin',
        }

  const chart_ids: Record<string, Record<string, string>> = lang === 'fr'
    ? {
        GVE: {
          hour: 'D2Qy8',
          day: 'AEqwF',
          month: 'iT4Ij',
          monthRain: 'XdG1K',
          year: '1AWte',
        },
        PAY: {
          hour: '39Nwj',
          day: 'hnp60',
          month: 'fbH3l',
          monthRain: 'ppr3S',
          year: 'TcZY3',
        },
        CGI: {
          hour: 'tl293',
          day: 'UKEMi',
          month: 'ufjNo',
          monthRain: 'oiHR1',
          year: 'IXIt8',
        },
        CHD: {
          hour: 'i8BT4',
          day: 'fefgd',
          month: 'DmosD',
          monthRain: 'rf2ke',
          year: 'l4BPD',
        },
        PUY: {
          hour: 'f9jl0',
          day: 'nCCuC',
          month: 'Izf23',
          monthRain: 'iNQT6',
          year: 'A1EuU',
        },
        LUG: {
          hour: 'DIpRk',
          day: 'R8QIU',
          month: 'cKdAW',
          monthRain: '3O0cM',
          year: 'M7RsH',
        },
        SMA: {
          hour: 'rM3pk',
          day: 'sPcv5',
          month: '5sNWT',
          monthRain: 'nf8xW',
          year: 'n09ci',
        },
      }
    : {
        SMA: {
          hour: '9ZJ24',
          day: 'H1icf',
          month: 'nngWI',
          monthRain: '0n6L1',
          year: '3f46Z',
        },
        BAS: {
          hour: 'Jl6Op',
          day: 'XUuq7',
          month: 'UFEwN',
          monthRain: '7EVQX',
          year: '30Xeh',
        },
        BER: {
          hour: 'luB1H',
          day: 'sAQ7u',
          month: 'nYoZD',
          monthRain: 'HKZhG',
          year: 'Y1k2M',
        },
        LUZ: {
          hour: 'Rwo9q',
          day: 'fQTGj',
          month: 'Hn83K',
          monthRain: 'fzc1F',
          year: 'cacPR',
        },
        STG: {
          hour: 'fIno3',
          day: 'pDtKc',
          month: 'GW8J3',
          monthRain: 'XUG48',
          year: 'wnVQ9',
        },
        GVE: {
          hour: '4u5tA',
          day: '2bz0d',
          month: 'aDuhT',
          monthRain: 'm1RWl',
          year: 'XxOBG',
        },
        LUG: {
          hour: 'DCtkv',
          day: 'y8Tn1',
          month: 'Nm17H',
          monthRain: 'W9N2U',
          year: 'fUTHg',
        },
      }

  // Chart order for debug
  const chartOrder = ['hour', 'day', 'month', 'monthRain', 'year']

  const defaultStation =
    lang === 'fr'
      ? tenant.name === 'Tribune de Genève'
        ? 'GVE'
        : 'PUY'
      : tenant.name === 'Berner Zeitung' ||
        tenant.name === 'Der Bund' ||
        tenant.name === 'Berner Oberländer' ||
        tenant.name === 'Langenthaler Tagblatt' ||
        tenant.name === 'Thuner Tagblatt'
      ? 'BER'
      : tenant.name === 'Basler Zeitung'
      ? 'BAS'
      : 'SMA'
  const [station, setStation] = useState(defaultStation)
  const [chartOpacity, setChartOpacity] = useState(1)
  const module = properties?.module
    ? properties?.module
    : localDebugMode
    ? 'intro'
    : ''
  const chartKey = properties?.chartKey ? properties?.chartKey : ''
  const currentChart = useRef<null | HTMLDivElement>(null)

  window.addEventListener(
    'stationChange',
    () => {
      if (window.currentStation !== undefined) {
        setStation(window.currentStation as string)
        // setChartOpacity(0)
      }
    },
    { once: true }
  )

  window.addEventListener('scrollToElement', () => {
    if (window.scrollTarget !== undefined) {
      if (chartKey === window.scrollTarget) {
        currentChart.current?.scrollIntoView({ behavior: 'smooth' })
      }
    }
  })

  const handleSelectStation = (s: string) => {
    setStation(s)
    window.currentStation = s
    window.dispatchEvent(new Event('stationChange'))
  }

  return (
    <AppContainer $colorMode={colorMode}>
      <TextWidthExtended>
        {module === 'intro' ? (
          <>
            <Toolbar
              buttons={stations}
              defaultButton={defaultStation}
              selectStation={handleSelectStation}
              lang={lang}
            />
            <IntroTitle>
              {lang === 'fr'
                ? 'Écart à la norme à ' + stations[station]
                : 'Abweichung zur Norm in ' + stations[station]}
              {Object.keys(station_locations).indexOf(station) >= 0 && (
                <span>
                  {' '}
                  ({lang === 'fr' ? 'Station de ' : 'Wetterstation '}
                  {station_locations[station]})
                </span>
              )}
            </IntroTitle>
            <p style={{ marginTop: 0 }}>
              {lang === 'fr'
                ? 'La norme correspond aux moyennes sur 30 ans de 1991 à 2020.'
                : 'Die Norm entspricht den 30-Jahres-Mittelwerten von 1991 bis 2020.'}
            </p>
            <Facts
              station={station}
              stationName={stations[station]}
              lang={lang}
            />
            {localDebugMode &&
              chartOrder.map((value) => (
                <div key={value}>
                  <p>
                    Chart ID: {chart_ids[station][value]}{' '}
                    <a
                      target='_blank'
                      href={`https://app.datawrapper.de/map/${chart_ids[station][value]}/visualize#refine`}
                      rel='noreferrer'
                    >
                      (edit)
                    </a>
                  </p>
                  <Chart>
                    <AutofitIFrame
                      style={{ opacity: chartOpacity }}
                      onLoad={() => {
                        setChartOpacity(1)
                      }}
                      src={`//datawrapper.dwcdn.net/${chart_ids[station][value]}/`}
                    />
                  </Chart>
                </div>
              ))}
          </>
        ) : (
          chartKey &&
          (chartKey === 'tropicalAnchor' ? (
            <div ref={currentChart}></div>
          ) : (
            <Chart
              id={chartKey}
              ref={currentChart}
              style={{ opacity: chartOpacity }}
            >
              <AutofitIFrame
                src={`//datawrapper.dwcdn.net/${chart_ids[station][chartKey]}/`}
              />
            </Chart>
          ))
        )}
      </TextWidthExtended>
    </AppContainer>
  )
}

const AppContainer = styled(TamediaStyles)`${GlobalVariables}
  // width: 100%;
  // max-width: 842px;
  margin: 0;
  margin-bottom: 2rem;
  // padding: 2em 0;
  font-size: 18px;
  font-family: var(--font-sans-serif);
  margin-left: auto;
  margin-right: auto;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  code {
    font-family: source-code-pro, Menlo, Monaco, Consolas, 'Courier New',
      monospace;
  }
  @keyframes fadeInOut {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
`

const Chart = styled.div`
  // background: var(--gray-4);
  // animation: fadeInOut 1s linear;
  iframe {
    transition: opacity 0.1s linear;
  }
`

const IntroTitle = styled.h3`
  margin-bottom: 1rem;
  span {
    font-weight: 400;
  }
`

export default MainComponent
